import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import SimpleHeader from './SimpleHeader';
import SimpleFooter from './SimpleFooter';
import '../styles/StoreLocator.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icon for user location
const createUserLocationIcon = () => {
  return L.divIcon({
    className: 'user-location-marker',
    html: '<div class="user-location-pulse"></div><div class="user-location-dot"></div>',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

// Component to handle map view changes
function MapView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  return null;
}

const StoreLocator = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTerritory, setSelectedTerritory] = useState('');
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [hoveredStore, setHoveredStore] = useState(null);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isUsingLocation, setIsUsingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [mapCenter, setMapCenter] = useState([32.8872, 13.1913]); // Tripoli coordinates
  const [mapZoom, setMapZoom] = useState(11);
  const [loading, setLoading] = useState(true);
  const mapSectionRef = useRef(null);
  
  // Arabic tile layer for better Arabic support
  const arabicTileLayer = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

  // Scroll to map section when filter panel opens
  useEffect(() => {
    if (isFilterPanelOpen && mapSectionRef.current) {
      setTimeout(() => {
        mapSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }, 100); // Small delay to ensure panel animation starts
    }
  }, [isFilterPanelOpen]);

  // Load stores from JSON file
  useEffect(() => {
    const loadStores = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/store locator.json`);
        const data = await response.json();
        
        // Transform JSON data to store format
        const transformedStores = data.map((store, index) => ({
          id: index + 1,
          name: store['Store - Business Name'] || 'متجر',
          address: store['Address'] || '',
          lat: parseFloat(store['Latitude']) || 0,
          lng: parseFloat(store['Longitude']) || 0,
          region: store['Region'] || '',
          territory: store['Territory'] || '',
        })).filter(store => store.lat !== 0 && store.lng !== 0); // Filter out invalid coordinates
        
        // Get unique territories
        const uniqueTerritories = [...new Set(transformedStores
          .map(store => store.territory)
          .filter(territory => territory && territory.trim() !== '')
        )].sort();
        
        setStores(transformedStores);
        setFilteredStores(transformedStores);
        setTerritories(uniqueTerritories);
        setLoading(false);
      } catch (error) {
        console.error('Error loading stores:', error);
        setLoading(false);
      }
    };

    loadStores();
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSelectedStoreId(null); // Clear store selection when searching
    setSelectedStore(null);
    setIsUsingLocation(false); // Clear location filter when searching
    setUserLocation(null);
    applyFilters(query, selectedTerritory, null, false);
  };

  const handleTerritoryChange = (e) => {
    const territory = e.target.value;
    setSelectedTerritory(territory);
    setSelectedStoreId(null); // Clear store selection when changing territory
    setSelectedStore(null);
    setIsUsingLocation(false); // Clear location filter when changing territory
    setUserLocation(null);
    applyFilters(searchQuery, territory, null, false);
  };

  // Calculate distance between two GPS coordinates using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in kilometers
    return distance;
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('La géolocalisation n\'est pas supportée par votre navigateur');
      return;
    }

    setIsUsingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation([latitude, longitude]);
        setIsUsingLocation(true);
        
        // Filter stores within 5 km radius
        const filtered = stores.filter(store => {
          const distance = calculateDistance(latitude, longitude, store.lat, store.lng);
          return distance <= 5; // 5 km radius
        });

        setFilteredStores(filtered);
        setSearchQuery('');
        setSelectedTerritory('');
        setSelectedStore(null);
        setSelectedStoreId(null);
        setMapCenter([latitude, longitude]);
        setMapZoom(13);
      },
      (error) => {
        setIsUsingLocation(false);
        setLocationError('Impossible d\'obtenir votre position. Veuillez vérifier les permissions de géolocalisation.');
        console.error('Geolocation error:', error);
      }
    );
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedTerritory('');
    setSelectedStore(null);
    setSelectedStoreId(null);
    setUserLocation(null);
    setIsUsingLocation(false);
    setLocationError(null);
    if (stores && stores.length > 0) {
      setFilteredStores(stores);
    }
    setMapCenter([32.8872, 13.1913]); // Reset to default Tripoli coordinates
    setMapZoom(11);
  };

  const applyFilters = (query, territory, storeId = null, useLocation = false) => {
    // Make sure stores are loaded
    if (!stores || stores.length === 0) {
      return;
    }

    let filtered = [...stores];

    // Filter by specific store ID first (when a store card is clicked)
    if (storeId !== null) {
      filtered = filtered.filter(store => store.id === storeId);
      setFilteredStores(filtered);
      if (filtered.length > 0) {
        setMapCenter([filtered[0].lat, filtered[0].lng]);
        setMapZoom(15);
      }
      return;
    }

    // Filter by location (5 km radius) if user location is active
    if (useLocation && userLocation) {
      filtered = filtered.filter(store => {
        const distance = calculateDistance(userLocation[0], userLocation[1], store.lat, store.lng);
        return distance <= 5; // 5 km radius
      });
    }

    // Filter by territory
    if (territory && territory !== '') {
      filtered = filtered.filter(store => store.territory === territory);
    }

    // Filter by search query (case-insensitive for Arabic and Latin text)
    if (query && query.trim() !== '') {
      const normalizedQuery = query.trim().toLowerCase();
      filtered = filtered.filter(store => {
        const name = (store.name || '').toLowerCase();
        const address = (store.address || '').toLowerCase();
        const storeTerritory = (store.territory || '').toLowerCase();
        const region = (store.region || '').toLowerCase();
        
        return name.includes(normalizedQuery) || 
               address.includes(normalizedQuery) ||
               storeTerritory.includes(normalizedQuery) ||
               region.includes(normalizedQuery);
      });
    }

    setFilteredStores(filtered);

    // Center map on first result if found
    if (filtered.length > 0) {
      setMapCenter([filtered[0].lat, filtered[0].lng]);
      setMapZoom((query && query.trim()) || territory || useLocation ? 12 : 11);
    } else if (!query && !territory && !useLocation) {
      // Reset to default view when no filters
      setMapCenter([32.8872, 13.1913]);
      setMapZoom(11);
    }
  };

  const handleStoreClick = (store) => {
    setSelectedStore(store);
    setSelectedStoreId(store.id);
    // Clear text search and territory filter to show only this specific store
    setSearchQuery('');
    setSelectedTerritory('');
    setIsUsingLocation(false);
    setUserLocation(null);
    // Filter to show only this specific store (by ID)
    applyFilters('', '', store.id, false);
  };

  return (
    <div className="store-locator-page">
      <SimpleHeader />
      
      <div className="store-locator-content">
        <section className="hero-section">
          <h1>ابحث عن متجر</h1>
          <p className="subtitle">اعثر على متجر يمكنك شراء TEREA منه بالقرب منك</p>
          <button
            className="use-location-btn"
            onClick={handleUseCurrentLocation}
            disabled={isUsingLocation}
          >
            {isUsingLocation ? (
              <>
                <span className="location-icon">📍</span>
                جاري تحديد الموقع...
              </>
            ) : (
              <>
                <span className="location-icon">📍</span>
                استخدم موقعي الحالي
              </>
            )}
          </button>
          {locationError && (
            <div className="location-error">
              {locationError}
            </div>
          )}
          <div className="search-container">
            <div className="search-wrapper">
              <input
                type="text"
                placeholder="ابحث عن متجر..."
                value={searchQuery}
                onChange={handleSearch}
                className="search-input"
              />
              {searchQuery && (
                <button
                  className="search-clear-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedStoreId(null);
                    setSelectedStore(null);
                    applyFilters('', selectedTerritory, null);
                  }}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="map-section" ref={mapSectionRef}>
            <div className={`map-container-wrapper ${isFilterPanelOpen ? 'panel-open' : ''}`}>
              <button
              className={`filter-toggle-btn ${isFilterPanelOpen ? 'open' : ''}`}
              onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
              aria-label="Toggle filter panel"
            >
              <span className="filter-icon">☰</span>
              <span className="filter-text">نقاط البيع</span>
              {!loading && (
                <span className="filter-badge">{filteredStores.length}</span>
              )}
            </button>

            <div className={`stores-list ${isFilterPanelOpen ? 'open' : ''}`}>
              <div className="stores-list-header">
                <h2>نقاط البيع</h2>
                <button 
                  className="close-filter-btn"
                  onClick={() => setIsFilterPanelOpen(false)}
                  aria-label="Close filter panel"
                >
                  ✕
                </button>
              </div>
              {!loading && (
                <div className="store-count-badge">
                  {filteredStores.length} {filteredStores.length === 1 ? 'متجر' : 'متاجر'}
                </div>
              )}
              <div className="territory-filter-container">
                <div className="filter-controls">
                  <select
                    value={selectedTerritory}
                    onChange={handleTerritoryChange}
                    className="territory-filter"
                  >
                    <option value="">جميع المناطق</option>
                    {territories.map((territory, index) => (
                      <option key={index} value={territory}>
                        {territory}
                      </option>
                    ))}
                  </select>
                  {(searchQuery || selectedTerritory || selectedStoreId || isUsingLocation) && (
                    <button
                      className="reset-filters-btn"
                      onClick={handleResetFilters}
                      aria-label="Reset filters"
                      title="إعادة تعيين الفلاتر"
                    >
                      ↻ إعادة تعيين
                    </button>
                  )}
                </div>
              </div>
              <div className="stores-grid">
                {loading ? (
                  <p className="no-results">جاري تحميل المتاجر...</p>
                ) : filteredStores.length > 0 ? (
                  filteredStores.map((store) => (
                    <div
                      key={store.id}
                      className={`store-card ${selectedStore?.id === store.id ? 'active' : ''} ${hoveredStore === store.id ? 'hovered' : ''}`}
                      onClick={() => handleStoreClick(store)}
                      onMouseEnter={() => setHoveredStore(store.id)}
                      onMouseLeave={() => setHoveredStore(null)}
                    >
                      <h3>{store.name}</h3>
                      <p>{store.address}</p>
                    </div>
                  ))
                ) : (
                  <p className="no-results">لا توجد نتائج</p>
                )}
              </div>
            </div>

            {isFilterPanelOpen && (
              <div 
                className="filter-overlay"
                onClick={() => setIsFilterPanelOpen(false)}
              ></div>
            )}

            <div className="map-container">
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <MapView center={mapCenter} zoom={mapZoom} />
              <TileLayer
                attribution=""
                url={arabicTileLayer}
              />
              {userLocation && (
                <Marker
                  position={userLocation}
                  icon={createUserLocationIcon()}
                >
                  <Popup>
                    <div className="popup-content">
                      <strong>موقعك الحالي</strong>
                    </div>
                  </Popup>
                </Marker>
              )}
              {filteredStores.map((store) => (
                <Marker
                  key={store.id}
                  position={[store.lat, store.lng]}
                  eventHandlers={{
                    click: () => handleStoreClick(store),
                    mouseover: () => setHoveredStore(store.id),
                    mouseout: () => setHoveredStore(null),
                  }}
                >
                  <Tooltip 
                    permanent={false}
                    direction="top"
                    offset={[0, -10]}
                    className="store-tooltip"
                    interactive={true}
                    opacity={1}
                  >
                    <div className="tooltip-card">
                      <h3 className="tooltip-title">{store.name}</h3>
                      <p className="tooltip-address">{store.address}</p>
                    </div>
                  </Tooltip>
                  <Popup>
                    <div className="popup-content">
                      <h3>{store.name}</h3>
                      <p>{store.address}</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
            </div>
          </div>
        </section>

        <section className="health-warning">
          <div className="health-warning-content">
            <div className="warning-text">
              <p className="warning-main">
                هذا المنتج ضار بالصحة ويؤدي إلى الإدمان
              </p>
              <p className="warning-sub">
                هذا المنتج غير خالٍ من الضرر يحتوي على النيكوتين، الذي يسبب الادمان
              </p>
              <p className="warning-age">
                لاستعمال المدخنين البالغين فقط
              </p>
            </div>
          </div>
        </section>

        <section className="question-section">
          <h2>للمزيد من المعلومات أو الاستفسارات</h2>
          <div className="question-content">
            <div className="question-box">
              <div className="instagram-logo-wrapper">
                <svg className="instagram-logo" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="currentColor"/>
                </svg>
              </div>
              <h3>سؤال؟</h3>
              <p>اتصل بنا على إنستغرام هنا</p>
              <a 
                href="https://www.instagram.com/iqos.libya/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="instagram-link"
              >
                <svg className="instagram-icon-small" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="currentColor"/>
                </svg>
                Instagram
              </a>
            </div>
          </div>
        </section>
      </div>

      <SimpleFooter />
    </div>
  );
};

export default StoreLocator;

